import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ApiService } from "app/services/api.service";
import { AlertHistory } from "app/models/alert-history";
import moment from "moment";
import { FormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatIconModule } from "@angular/material/icon";
import { Subscription } from "rxjs";
import { ControlRoomService } from "app/services/control-room.service";

@Component({
    selector: "app-alarm-history",
    imports: [CommonModule, FormsModule,
        MatExpansionModule,
        MatTooltipModule,
        TranslateModule, MatIconModule],
    templateUrl: "./alarm-history.component.html",
    styleUrls: ["./alarm-history.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None
})
export class AlarmHistoryComponent implements OnChanges, OnInit, OnDestroy {
  @Input() deviceId!: string;
  @Input() customerId!: string;
  private fetchHistorySub!: Subscription;
  history: AlertHistory[] = [];
  alarms: { reasons: { reason: string; category: string }[]; comments?: { user: string; text: string }[]; timestamp: string; collapsed: boolean; showToggle: boolean; }[] = [];
  loading = false;
  expanded = false;
  reasonMap: Record<string, string> = {};

  constructor(private api: ApiService, private translate: TranslateService, private controlRoomService: ControlRoomService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['deviceId'] && changes['deviceId'].currentValue) {
      this.deviceId = changes['deviceId'].currentValue;
      this.history = [];
      this.alarms = [];
    }
  }

  ngOnInit() {
    this.fetchHistorySub = this.controlRoomService.fetchAlertHistoryForAlert.subscribe(() => {
      if (this.deviceId) {
        this.fetchHistory();
      }
    });
  }

  public fetchHistory() {
    this.loading = true;

    const user = JSON.parse(localStorage.getItem('user'));
    const aliasList: any[] = user?.aliasList ?? [];
    const alias = aliasList.find(a => a.customerId === this.customerId);
    const userId: string = alias?.userId ?? '';
    const customerId: string = alias?.customerId ?? '';
    const locationId: string = alias?.locationId ?? '';
  
    const lang = this.translate.currentLang || 'en';
    this.api.getAlarmReasons(lang).subscribe({
      next: (res: any) => {
        const parsed = typeof res === 'string' ? JSON.parse(res) : res;
        this.reasonMap = this.buildReasonMap(parsed);

        this.api.getAlarmHistory(this.deviceId, userId, customerId, locationId).subscribe({
          next: (data: AlertHistory[]) => {
            this.history = data;
            this.alarms = data.map(a => {
              const commentsArray = (a.commentEvents || [])
                .map(c => ({
                  user: c.UserName,
                  text: c.Comment?.trim()
                }))
                .filter(c => c.text);
            
              const comments = commentsArray.length > 0
                ? commentsArray
                : [{ user: '', text: this.translate.instant('NO_COMMENT') }];
        
                  return {
                    reasons: a.alarmReasons?.length
                      ? a.alarmReasons.map(r => {
                          const category =
                            this.reasonMap[r.alarmReasonCategory] || r.alarmReasonCategory;
        
                          const reason =
                            this.reasonMap[r.alarmReason] || r.alarmReason;
        
                          return { category, reason };
                        })
                      : [{ category: '', reason: this.translate.instant('NO_REASON') }],
                      
                    comments: comments,
                    timestamp: a.receiveTime
                      ? moment(a.receiveTime).format('MMM DD, HH:mm')
                      : '',
                    collapsed: true,
                    showToggle: comments.length > 2
                  };
                });
            this.loading = false;
          },
          error: err => {
            console.error('Failed to load alarm history', err);
            this.loading = false;
            this.alarms = [];
          }
        });
      },
      error: err => {
        console.error('Failed to load alarm reasons', err);
        this.loading = false;
      }
    });
  }

  private buildReasonMap(data: any[]): Record<string, string> {
  const map: Record<string, string> = {};
  const lang = this.translate.currentLang?.toLowerCase();

  data.forEach(category => {
    let categoryValue = '';

    switch (lang) {
      case 'fi':
        categoryValue = category.alarmReasonCategory_FI;
        break;
      case 'sv':
      case 'se':
        categoryValue = category.alarmReasonCategory_SE;
        break;
      case 'en':
      default:
        categoryValue = category.alarmReasonCategory_EN;
        break;
    }

    map[category.dictionaryEntryKey] = categoryValue;
    category.alarmReasons.forEach(r => {
      let value = '';

      switch (lang) {
        case 'fi':
          value = r.alarmReason_FI;
          break;
        case 'sv':
        case 'se':
          value = r.alarmReason_SE;
          break;
        case 'en':
        default:
          value = r.alarmReason_EN;
          break;
      }

      map[r.dictionaryEntryKey] = value;
    });
  });

  return map;
}

  ngOnDestroy() {
    this.fetchHistorySub?.unsubscribe();
  }
}
